import { FC, useState, useEffect, useRef } from "react";
import { TFunction } from "i18next";
import { currentLanguage } from "../../../i18n/type";
import { ROUTES } from "../../../routes";
import "./style.css";

interface CarouselComponentProps {
  maxWidth: number;
  screenWidth: number;
  screenHeight: number;
  t: TFunction<"translation", undefined>;
  isSecondary?: boolean;
}

const CarouselComponent: FC<CarouselComponentProps> = (
  props: CarouselComponentProps
) => {
  const { t, maxWidth, screenWidth, screenHeight, isSecondary = false } = props;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Enhanced carousel data with visual elements
  const slides = isSecondary
    ? [
        {
          id: 1,
          title: t("homePage.secondSlider.slide1.title"),
          description: t("homePage.secondSlider.slide1.description"),
          image: "/images/slider1.jpg",
          icon: "🌐",
          features: t("homePage.secondSlider.slide1.features", {
            returnObjects: true,
          }) as string[],
          color: "rgba(19, 29, 51, 0.9)",
        },
        {
          id: 2,
          title: t("homePage.secondSlider.slide2.title"),
          description: t("homePage.secondSlider.slide2.description"),
          image: "/images/slider2.jpg",
          icon: "📚",
          features: t("homePage.secondSlider.slide2.features", {
            returnObjects: true,
          }) as string[],
          color: "rgba(19, 29, 51, 0.85)",
        },
        {
          id: 3,
          title: t("homePage.secondSlider.slide3.title"),
          description: t("homePage.secondSlider.slide3.description"),
          image: "/images/slider3.jpg",
          icon: "⚡",
          features: t("homePage.secondSlider.slide3.features", {
            returnObjects: true,
          }) as string[],
          color: "rgba(19, 29, 51, 0.8)",
        },
      ]
    : [
        {
          id: 1,
          title: t("homePage.slider.slide1.title"),
          description: t("homePage.slider.slide1.description"),
          image: "/images/slider1.jpg",
          icon: "🌍",
          features: t("homePage.slider.slide1.features", {
            returnObjects: true,
          }) as string[],
          color: "rgba(19, 29, 51, 0.9)",
        },
        {
          id: 2,
          title: t("homePage.slider.slide2.title"),
          description: t("homePage.slider.slide2.description"),
          image: "/images/slider2.jpg",
          icon: "👩‍💼",
          features: t("homePage.slider.slide2.features", {
            returnObjects: true,
          }) as string[],
          color: "rgba(19, 29, 51, 0.85)",
        },
        {
          id: 3,
          title: t("homePage.slider.slide3.title"),
          description: t("homePage.slider.slide3.description"),
          image: "/images/slider3.jpg",
          icon: "🚀",
          features: t("homePage.slider.slide3.features", {
            returnObjects: true,
          }) as string[],
          color: "rgba(19, 29, 51, 0.8)",
        },
      ];

  // Auto-advance slides
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isTransitioning) {
        nextSlide();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isTransitioning, slides.length]);

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const handleContactClick = () => {
    window.location.href = `${window.location.origin}/${currentLanguage}/${ROUTES.contact[currentLanguage]}`;
  };

  return (
    <div className="carousel-container">
      <div className="carousel-wrapper" ref={carouselRef}>
        <div
          className="carousel-track"
          style={{
            transform: `translateX(-${currentSlide * 100}%)`,
            transition: isTransitioning ? "transform 0.5s ease-in-out" : "none",
          }}
        >
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className="carousel-slide"
              style={{
                backgroundImage: `url(${slide.image})`,
                background: `linear-gradient(135deg, ${slide.color}20 0%, ${slide.color}40 100%), url(${slide.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                minHeight: isSecondary ? 400 : 500,
                width: "100%",
              }}
            >
              <div className="slide-overlay">
                <div className="slide-content">
                  {/* Icon and Title Section */}
                  <div className="slide-header">
                    <div
                      className="slide-icon"
                      style={{
                        backgroundColor: slide.color,
                        animationDelay: "0.2s",
                      }}
                    >
                      <span className="icon-text">{slide.icon}</span>
                    </div>
                    <h2 className="slide-title">{slide.title}</h2>
                  </div>

                  <p className="slide-description">{slide.description}</p>

                  {/* Features List */}
                  <div className="slide-features">
                    {slide.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="feature-item"
                        style={{
                          animationDelay: `${0.4 + featureIndex * 0.1}s`,
                        }}
                      >
                        <span className="feature-icon">✓</span>
                        <span className="feature-text">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Contact Button */}
                  <div className="contact-button-container mb-3">
                    <button
                      className="btn btn-primary btn-lg contact-button"
                      onClick={handleContactClick}
                      style={{
                        fontSize: screenWidth <= 576 ? "1rem" : "1.25rem",
                        padding:
                          screenWidth <= 576 ? "0.75rem 1.5rem" : "1rem 2rem",
                        backgroundColor: slide.color,
                        borderColor: slide.color,
                        animationDelay: "0.8s",
                      }}
                    >
                      {t("general.contactButtonText")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          className={`carousel-nav prev ${isTransitioning ? "disabled" : ""}`}
          onClick={prevSlide}
          disabled={isTransitioning}
        >
          <span>&lt;</span>
        </button>
        <button
          className={`carousel-nav next ${isTransitioning ? "disabled" : ""}`}
          onClick={nextSlide}
          disabled={isTransitioning}
        >
          <span>&gt;</span>
        </button>

        {/* Dots Indicator */}
        <div className="carousel-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentSlide ? "active" : ""} ${
                isTransitioning ? "disabled" : ""
              }`}
              onClick={() => goToSlide(index)}
              disabled={isTransitioning}
            />
          ))}
        </div>

        {/* Progress Bar */}
        <div className="carousel-progress">
          <div
            className="progress-bar"
            style={{
              width: `${((currentSlide + 1) / slides.length) * 100}%`,
              transition: isTransitioning ? "width 0.5s ease-in-out" : "none",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CarouselComponent;
